/**
 * Message - Compact single-line chat / system message. Optional 24px avatar
 * circle, bold name with configurable colour, and plain message body.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export interface MessageContent {
  /** Sender name shown in bold. */
  name?: string;
  /** Message body text. */
  body: string;
  /** CSS colour applied to the name (e.g. team colour). */
  color?: string;
  /** SVG markup injected into the 24px avatar circle. */
  iconHtml?: string;
}

export interface MessageOptions extends BaseUIComponentOptions {}

export class Message extends BaseUIComponent<MessageOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Message',
    category: 'hud',
    summary: 'Compact single-line chat/system message with optional avatar and bold sender name.',
    useCases: ['chat', 'message', 'system message', 'kill feed', 'notification line', 'hud text'],
    optionsType: 'MessageOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/message.html',
      styles: '@engine/assets/ui/styles/message.css',
    },
  };

  private avatarElement: HTMLElement | null = null;
  private nameElement: HTMLElement | null = null;
  private bodyElement: HTMLElement | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: Message.metadata.assetPaths.template,
      stylesPath: Message.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<MessageOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {};
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.avatarElement = this.layout.querySelector('[data-message-avatar]') as HTMLElement | null;
    this.nameElement = this.layout.querySelector('[data-message-name]') as HTMLElement | null;
    this.bodyElement = this.layout.querySelector('[data-message-body]') as HTMLElement | null;
  }

  /**
   * Populate the message with new content. All fields are optional except
   * `body`.
   */
  public setMessage(content: MessageContent): void {
    const { name = '', body, color, iconHtml = '' } = content;

    if (this.avatarElement) {
      this.avatarElement.innerHTML = iconHtml;
      this.avatarElement.style.display = iconHtml ? '' : 'none';
    }

    if (this.nameElement) {
      this.nameElement.textContent = name;
      this.nameElement.style.color = color ?? '';
      this.nameElement.style.display = name ? '' : 'none';
    }

    if (this.bodyElement) {
      this.bodyElement.textContent = body;
    }
  }

  protected override onDestroy(): void {
    this.avatarElement = null;
    this.nameElement = null;
    this.bodyElement = null;
  }
}
